import React, { useState } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';
import SectionContainer from '../../containers/PageBuilder/SectionBuilder/SectionContainer';
import { NamedLink } from '../../components';

import { STORIES, UNIQUE_POINTS } from '../howItWorks';
import gem from '../salesPage.module.css';
import css from './SectionHowItWorks.module.css';

/**
 * Das Zeichen im Kreis sagt, um was fuer eine Szene es geht: Ausgangslage,
 * der alte Weg (Kreuz), ein Schritt im Portal (Ziffer) oder das Ergebnis
 * (Haken). Wer die Spalte herunterliest, erkennt den Verlauf schon an den
 * Zeichen, bevor er ein Wort gelesen hat.
 */
const SceneMark = ({ kind, nummer }) => {
  if (kind === 'old') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path
          d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (kind === 'result') {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <path
          d="M4 9.3 7.1 12.4 14 5.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (kind === 'situation') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="3.4" fill="currentColor" />
      </svg>
    );
  }
  return <span className={css.markNum}>{nummer}</span>;
};

/**
 * FairwayBid: "So funktioniert es" als zwei Vorgaenge.
 *
 * Eine Aufzaehlung erklaert, was das Portal tut. Eine Geschichte zeigt,
 * warum jemand es braucht. Deshalb zwei reale Vorgaenge, einer je Seite des
 * Marktplatzes - und beide auf dieselbe Beschaffung bezogen: derselbe
 * kaputte Ballautomat, einmal aus Sicht der Anlage, einmal aus Sicht des
 * Haendlers. Wer umschaltet, sieht dieselbe Woche von der anderen Seite.
 *
 * Jede Geschichte enthaelt eine Szene "So lief das bisher". Die ist der
 * Punkt, an dem es klickt: Nicht was FairwayBid kann, ueberzeugt, sondern
 * was ohne FairwayBid passiert.
 *
 * Umschalter statt beide Spalten nebeneinander: Nebeneinander liest man
 * keine der beiden zu Ende. So liest man eine ganz - und die andere, weil
 * man neugierig ist.
 */
const SectionHowItWorks = props => {
  const {
    sectionId,
    className,
    rootClassName,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  const [aktiv, setAktiv] = useState(STORIES[0].key);
  const story = STORIES.find(s => s.key === aktiv) || STORIES[0];

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  // Nur die Schritte im Portal werden gezaehlt; Ausgangslage, alter Weg und
  // Ergebnis sind keine Schritte und bekommen ihr eigenes Zeichen.
  let schrittZaehler = 0;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={gem.header}>
          <span className={gem.eyebrow}>In zwei Vorgängen erklärt</span>
          <Field data={title} className={gem.headTitle} options={fieldOptions} />
          <span className={gem.rule} aria-hidden="true" />
          <Field data={description} className={gem.intro} options={fieldOptions} />
        </header>
      ) : null}

      <div className={gem.wrap}>
        <p className={css.hint}>
          Dieselbe Woche, dieselbe Beschaffung – einmal aus Sicht der Golfanlage, einmal aus Sicht
          des Händlers. Wählen Sie eine Seite.
        </p>

        <div className={css.tabs} role="tablist" aria-label="Zwei Sichtweisen">
          {STORIES.map(s => (
            <button
              key={s.key}
              type="button"
              role="tab"
              id={`fwb-tab-${s.key}`}
              aria-selected={s.key === aktiv}
              aria-controls={`fwb-panel-${s.key}`}
              className={classNames(css.tab, { [css.tabActive]: s.key === aktiv })}
              onClick={() => setAktiv(s.key)}
            >
              {s.tab}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id={`fwb-panel-${story.key}`}
          aria-labelledby={`fwb-tab-${story.key}`}
          className={css.panel}
        >
          <p className={css.who}>{story.who}</p>

          <ol className={css.scenes}>
            {story.scenes.map(scene => {
              if (scene.kind === 'step') {
                schrittZaehler += 1;
              }
              return (
                <li
                  key={scene.title}
                  className={classNames(css.scene, {
                    [css.sceneOld]: scene.kind === 'old',
                    [css.sceneResult]: scene.kind === 'result',
                  })}
                >
                  <span className={css.mark} aria-hidden="true">
                    <SceneMark kind={scene.kind} nummer={schrittZaehler} />
                  </span>
                  <div className={css.sceneBody}>
                    <span className={css.sceneTime}>{scene.time}</span>
                    <h3 className={css.sceneTitle}>{scene.title}</h3>
                    <p className={css.sceneText}>{scene.text}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <div className={css.unique}>
        <div className={css.uniqueInner}>
          <h3 className={css.uniqueTitle}>Warum das anderswo nicht geht</h3>
          <div className={css.uniqueGrid}>
            {UNIQUE_POINTS.map(p => (
              <article key={p.headline} className={css.uniqueItem}>
                <h4 className={css.uniqueHeadline}>{p.headline}</h4>
                <p className={css.uniqueText}>{p.text}</p>
              </article>
            ))}
          </div>
          <NamedLink name="SignupPage" className={css.uniqueCta}>
            Jetzt registrieren
          </NamedLink>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionHowItWorks;
